import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { DELEGATE_ELIGIBLE_ROLES } from '../../config/appRoutes';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import { useMutation } from '../../hooks/useMutation';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import DirectorPanelState from './DirectorPanelState';
import { useConfirm } from '../ui/ConfirmDialog';

const ALL_DIRECTOR_LOCATIONS = 'ALL';
const AUTHORITY_OPTIONS = [
  { value: 'goal', label: 'Goal Approval' },
  { value: 'flagged_actual', label: 'Flagged Actual Approval' },
  { value: 'attendance_event', label: 'Attendance / PTO Event Approval' },
  { value: 'performance_impact', label: 'Performance Impact Approval' }
];
const MODE_OPTIONS = [
  { value: 'backup_authority', label: 'Backup Permissions' },
  { value: 'temporary_reassignment', label: 'Temporary Reassignment' }
];

function toLocalDateTimeValue(date) {
  const safeDate = date instanceof Date ? date : new Date(date);
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  const hours = String(safeDate.getHours()).padStart(2, '0');
  const minutes = String(safeDate.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function createEmptyForm(defaultLocationId = '') {
  const now = new Date();
  const end = new Date(now.getTime() + (24 * 60 * 60 * 1000));
  return {
    locationId: defaultLocationId,
    delegateUserId: '',
    authorityType: 'goal',
    delegationMode: 'backup_authority',
    startAt: toLocalDateTimeValue(now),
    endAt: toLocalDateTimeValue(end),
    note: ''
  };
}

function formatWords(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function DirectorDelegationsPanel({
  selectedLocationCode = ALL_DIRECTOR_LOCATIONS,
  locationLabel = 'Company Rollup'
}) {
  const confirm = useConfirm();
  const [form, setForm] = useState(createEmptyForm(''));
  const [revokingId, setRevokingId] = useState('');

  const query = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
    ? `?locationCode=${encodeURIComponent(selectedLocationCode)}`
    : '';

  const { data: fetchedData, isLoading, error: fetchError, refetch } = useMultiFetch({
    delegations: `/api/delegations${query}`,
    users: `/api/users${query}`,
    locations: '/api/locations',
  }, { deps: [selectedLocationCode] });

  const delegations = useMemo(
    () => (Array.isArray(fetchedData.delegations) ? fetchedData.delegations : []),
    [fetchedData.delegations]
  );
  const users = useMemo(
    () => (Array.isArray(fetchedData.users) ? fetchedData.users : []),
    [fetchedData.users]
  );
  const locations = useMemo(
    () => (Array.isArray(fetchedData.locations) ? fetchedData.locations : []),
    [fetchedData.locations]
  );

  const scopedLocations = useMemo(
    () => locations.filter((location) => (
      location.isActive && (
        selectedLocationCode === ALL_DIRECTOR_LOCATIONS
        || location.code === selectedLocationCode
      )
    )),
    [locations, selectedLocationCode]
  );

  useEffect(() => {
    if (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS && scopedLocations[0] && !form.locationId) {
      setForm(current => ({
        ...current,
        locationId: String(scopedLocations[0].id)
      }));
    }
  }, [form.locationId, scopedLocations, selectedLocationCode]);

  const eligibleUsers = useMemo(
    () => users.filter((user) => (
      user.isActive
      && DELEGATE_ELIGIBLE_ROLES.has(user.role)
      && (
        selectedLocationCode === ALL_DIRECTOR_LOCATIONS
        || user.locationCode === selectedLocationCode
        || (user.assignedLocations || []).some(location => location.locationCode === selectedLocationCode)
      )
    )),
    [selectedLocationCode, users]
  );

  const {
    mutate: createDelegation,
    isLoading: isSubmitting,
    error: submitError,
    successMessage: createBanner
  } = useMutation(
    (payload) => apiRequest('/api/delegations', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
    {
      successMessage: 'Delegation created.',
      errorFallback: 'Unable to create delegation.',
      onSuccess: () => {
        setForm(createEmptyForm(selectedLocationCode !== ALL_DIRECTOR_LOCATIONS && scopedLocations[0]
          ? String(scopedLocations[0].id)
          : ''));
        refetch();
      }
    }
  );

  const {
    mutate: revokeDelegation,
    error: revokeError,
    successMessage: revokeBanner
  } = useMutation(
    (delegationId) => apiRequest(`/api/delegations/${delegationId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({})
    }),
    {
      successMessage: 'Delegation revoked.',
      errorFallback: 'Unable to revoke delegation.',
      onSuccess: () => { refetch(); },
      onError: () => setRevokingId(''),
    }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    await createDelegation({
      locationId: Number(form.locationId),
      delegateUserId: Number(form.delegateUserId),
      authorityType: form.authorityType,
      delegationMode: form.delegationMode,
      startAt: new Date(form.startAt).toISOString(),
      endAt: new Date(form.endAt).toISOString(),
      note: form.note || undefined
    });
  };

  const handleRevoke = async (delegation) => {
    const delegateLabel = delegation?.delegateName || delegation?.delegateEmail || 'this delegate';
    const confirmed = await confirm(
      `Revoke delegated approval permissions from ${delegateLabel}? They will lose these delegated permissions immediately.`,
      { title: 'Revoke delegation', confirmLabel: 'Revoke', tone: 'danger' }
    );
    if (!confirmed) {
      return;
    }

    setRevokingId(String(delegation.id));
    await revokeDelegation(delegation.id);
    setRevokingId('');
  };

  const banner = createBanner || revokeBanner;
  const error = submitError || revokeError || fetchError;

  return (
    <div className="role-section director-delegations-section">
      <div className="role-section-header">
        <h2>Delegation Coverage</h2>
        <div className="role-section-actions">
          <span>{delegations.length} in scope</span>
        </div>
      </div>
      <p className="role-section-copy">
        Create temporary, location-specific approval coverage without transferring location ownership.
      </p>
      <p className="director-muted">
        Scope: {selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? locationLabel : 'Company Rollup'}
      </p>
      <DirectorPanelState kind="success" message={banner} />
      <DirectorPanelState
        kind="error"
        message={error}
        onRetry={error && error === fetchError ? refetch : undefined}
      />

      <div className="director-secondary-grid director-delegation-grid">
        <div className="director-card">
          <div className="role-section-header">
            <h2>Create Delegation</h2>
            <span>Approval-only</span>
          </div>
          <form className="director-admin-form director-delegation-form" onSubmit={handleSubmit}>
            <label>
              Location
              <select
                value={form.locationId}
                onChange={(event) => setForm(current => ({ ...current, locationId: event.target.value }))}
                required
                disabled={isSubmitting}
              >
                <option value="">Select location</option>
                {scopedLocations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.code} - {location.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Delegate
              <select
                value={form.delegateUserId}
                onChange={(event) => setForm(current => ({ ...current, delegateUserId: event.target.value }))}
                required
                disabled={isSubmitting}
              >
                <option value="">Select delegate</option>
                {eligibleUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.email} ({formatWords(user.role)})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Permission Type
              <select
                value={form.authorityType}
                onChange={(event) => setForm(current => ({ ...current, authorityType: event.target.value }))}
                required
                disabled={isSubmitting}
              >
                {AUTHORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              Delegation Mode
              <select
                value={form.delegationMode}
                onChange={(event) => setForm(current => ({ ...current, delegationMode: event.target.value }))}
                required
                disabled={isSubmitting}
              >
                {MODE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              Start
              <input
                type="datetime-local"
                value={form.startAt}
                onChange={(event) => setForm(current => ({ ...current, startAt: event.target.value }))}
                required
                disabled={isSubmitting}
              />
            </label>

            <label>
              End
              <input
                type="datetime-local"
                value={form.endAt}
                onChange={(event) => setForm(current => ({ ...current, endAt: event.target.value }))}
                required
                disabled={isSubmitting}
              />
            </label>

            <label className="director-admin-form-full">
              Note
              <textarea
                value={form.note}
                onChange={(event) => setForm(current => ({ ...current, note: event.target.value }))}
                placeholder="Optional coverage note."
                rows={3}
                disabled={isSubmitting}
              />
            </label>

            <div className="director-table-actions">
              <Button size="sm" type="submit" loading={isSubmitting} loadingLabel="Saving…">
                Create Delegation
              </Button>
            </div>
          </form>
        </div>

        <div className="director-card">
          <div className="role-section-header">
            <h2>Active and Recent Delegations</h2>
            <span>{isLoading ? 'Loading…' : `${delegations.length} records`}</span>
          </div>
          <DataTable
            tableClassName="director-delegation-table"
            emptyMessage="No delegations are currently in scope."
            isEmpty={!delegations.length}
            columns={[
              { label: 'Location' },
              { label: 'Permissions' },
              { label: 'Delegate' },
              { label: 'Mode' },
              { label: 'Window' },
              { label: 'Delegation state' },
              { label: 'Note' },
              { label: 'Action' }
            ]}
          >
            {delegations.map(delegation => (
              <tr key={delegation.id}>
                <td>{delegation.locationCode}</td>
                <td>{formatWords(delegation.authorityType)}</td>
                <td>{delegation.delegateUserEmail}</td>
                <td>{formatWords(delegation.delegationMode)}</td>
                <td>
                  {delegation.startAt ? new Date(delegation.startAt).toLocaleString() : 'n/a'}
                  <br />
                  {delegation.endAt ? new Date(delegation.endAt).toLocaleString() : 'n/a'}
                </td>
                <td>
                  <StatusPill tone={delegation.status === 'active' ? 'active' : 'inactive'}>
                    {delegation.status}
                  </StatusPill>
                </td>
                <td>{delegation.note || 'No note'}</td>
                <td>
                  {delegation.status === 'active' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={revokingId === String(delegation.id)}
                      loadingLabel="Revoking..."
                      onClick={() => handleRevoke(delegation)}
                    >
                      Revoke
                    </Button>
                  ) : (
                    <span className="director-muted">Closed</span>
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      </div>
    </div>
  );
}

export default DirectorDelegationsPanel;
